import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  MenuItem,
  Paper,
  Stack,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import RefreshRoundedIcon from "@mui/icons-material/RefreshRounded";
import IntegrationApi from "../apis/integrationApi";
import { useUserMsp } from "../hooks/useUserMsp";
import { useAgents } from "../hooks/useAgents";
import { NEUTRAL, VIOLET } from "../styles/brandTokens";
import { isHorizonRootRole } from "../utils/horizonRoles";
import ExecutionFilterBar from "./monitor/ExecutionFilterBar";
import ExecutionQueue from "./monitor/ExecutionQueue";
import SummaryPanel from "./monitor/SummaryPanel";
import useVisiblePoll from "./monitor/useVisiblePoll";
import {
  AutoAgentFunnelEvent,
  AutoAgentFunnelSummary,
  AutoAgentMonitorFilterOptions,
} from "../types/integration";
import {
  EMPTY_EXECUTION_FILTERS,
  ExecutionFilters,
  buildMonitorQueryParams,
} from "./theoAutomationsMonitorFilters";

// Page-local aliases for the brand tokens: violet is the interactive color, borders and muted
// text use the plum-tinted neutrals so this page matches the ticket and chat surfaces.
const BROWN = VIOLET.main;
const BROWN_TINT = VIOLET.tint;
const BORDER = NEUTRAL.border;
const MUTED = NEUTRAL.textSecondary;
const STRIP_BG = NEUTRAL.subtle;
const DEFAULT_PAGE_SIZE = 25;

const integrationApi = new IntegrationApi();

const parseApiTimestamp = (value: string) => {
  const trimmed = value.trim();
  const hasTimezone = /(?:Z|[+-]\d{2}:?\d{2})$/i.test(trimmed);
  const normalized = hasTimezone ? trimmed : `${trimmed.replace(" ", "T")}Z`;
  return new Date(normalized);
};

const formatDateTime = (value?: string | null) => {
  if (!value) return "-";
  const date = parseApiTimestamp(value);
  if (Number.isNaN(date.getTime())) return "-";
  return date.toLocaleString(undefined, {
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
};

const FUNNEL_WINDOWS = [
  { value: "1h", label: "1 hour" },
  { value: "6h", label: "6 hours" },
  { value: "24h", label: "24 hours" },
  { value: "48h", label: "48 hours" },
  { value: "7d", label: "7 days" },
];

const FUNNEL_STAGES = [
  { value: "", label: "All stages" },
  { value: "out_of_scope", label: "2a. Scope: out of scope" },
  { value: "skipped_existing_state", label: "3d. In scope: existing-state skip" },
  { value: "classified_drop", label: "5a. Classified: no Theo handling" },
  { value: "qualified", label: "5b. Classified: qualified" },
  { value: "awaiting_assignment", label: "5c. Assignment: waiting for the queue" },
  { value: "dropped", label: "5c. Classified: dropped before execution" },
  { value: "execution_started", label: "6. Execution: started" },
  { value: "execution_queued", label: "6a. Execution: queued" },
  { value: "execution_completed", label: "6b. Execution: completed" },
  { value: "execution_failed", label: "6c. Execution: failed" },
  { value: "execution_error", label: "6d. Execution: error" },
];

const formatNumber = (value?: number | null) => Number(value || 0).toLocaleString();

const formatStage = (value?: string | null) => {
  switch (value) {
    case "fetched":
      return "1. Fetch: fetched from PSA";
    case "in_scope":
      return "2b. Scope: in scope";
    case "detected":
      return "4. Event: detected actionable";
    case "qualified":
      return "5b. Classified: qualified";
    case "awaiting_assignment":
      return "5c. Assignment: waiting for the queue";
    case "execution_started":
      return "6. Execution: started";
    case "execution_queued":
      return "6a. Execution: queued";
    case "executed":
      return "6a. Execution: queued";
    case "execution_completed":
      return "6b. Execution: completed";
    case "classified_drop":
      return "5a. Classified: no Theo handling";
    case "out_of_scope":
      return "2a. Scope: out of scope";
    case "execution_error":
      return "6d. Execution: error";
    case "execution_failed":
      return "6c. Execution: failed";
    case "skipped_existing_state":
      return "3d. In scope: existing-state skip";
    case "dropped":
      return "5c. Classified: dropped";
    case "unchanged":
      return "3c. In scope: unchanged";
    case "baseline":
      return "3a. In scope: baseline";
    case "outside_window":
      return "3b. In scope: outside window";
    case "errors":
      return "6c. Execution: cycle errors";
    default:
      return value ? value.replace(/_/g, " ") : "-";
  }
};

const formatDecision = (row: AutoAgentFunnelEvent) =>
  row.selected_mode || row.decision || row.dropoff_reason || row.event_stage || "-";

const formatTheoCategoryNode = (category: {
  l1?: string | null;
  l2?: string | null;
  confidence?: string | null;
}) => {
  const l1 = String(category?.l1 || "").trim();
  const l2 = String(category?.l2 || "").trim();
  const confidence = String(category?.confidence || "").trim();
  if (!l1) return "";
  const base = l2 ? `${l1} > ${l2}` : l1;
  return confidence ? `${base} (${confidence})` : base;
};

/** Full multi-line Theo category text for tooltip / native title (ALT) hover. */
const formatTheoCategoryTooltip = (row: AutoAgentFunnelEvent) => {
  const categories = Array.isArray(row.theo_category) ? row.theo_category : [];
  const lines = categories.map(formatTheoCategoryNode).filter(Boolean);
  if (lines.length > 0) {
    return lines.join("\n");
  }
  return String(row.theo_category_label || "").trim();
};

const FunnelTheoCategoryCell: React.FC<{ row: AutoAgentFunnelEvent }> = ({ row }) => {
  const theoTooltip = formatTheoCategoryTooltip(row);
  const matchedLine = row.matched_theo_category_label
    ? `Matched ${row.matched_theo_category_label}`
    : row.funnel_stage === "out_of_scope"
    ? "Not classified (rejected at PSA scope)"
    : "";
  const title = [theoTooltip, matchedLine].filter(Boolean).join("\n");

  return (
    <Tooltip
      title={
        title ? (
          <Box sx={{ whiteSpace: "pre-line", fontFamily: "var(--theo-font-sans)", fontSize: 12.5, maxWidth: 420 }}>
            {title}
          </Box>
        ) : (
          ""
        )
      }
      disableHoverListener={!title}
      arrow
    >
      <Box title={title || undefined} sx={{ minWidth: 0, cursor: title ? "help" : "default" }}>
        <Typography sx={{ fontFamily: "var(--theo-font-sans)", fontSize: 13.5 }} noWrap>
          {row.theo_category_label || "-"}
        </Typography>
        {matchedLine ? (
          <Typography sx={{ fontFamily: "var(--theo-font-sans)", fontSize: 11.5, color: MUTED }} noWrap>
            {matchedLine}
          </Typography>
        ) : null}
      </Box>
    </Tooltip>
  );
};

const FunnelHierarchyGuide: React.FC = () => (
  <Paper elevation={0} sx={{ border: `1px solid ${BORDER}`, borderRadius: 2, p: 2, background: "#fff" }}>
    <Typography sx={{ fontFamily: "var(--theo-font-sans)", fontSize: 16, fontWeight: 800, mb: 1 }}>
      How to read this funnel
    </Typography>
    <Stack spacing={0.75}>
      <Typography sx={{ fontFamily: "var(--theo-font-sans)", fontSize: 13.5 }}>
        <strong>1. Fetch:</strong> tickets returned by the PSA poll query for this cycle/window.
      </Typography>
      <Typography sx={{ fontFamily: "var(--theo-font-sans)", fontSize: 13.5 }}>
        <strong>2. Scope check:</strong> fetched tickets split into <strong>out of scope</strong> or <strong>in scope</strong> based on configured queues/categories.
      </Typography>
      <Typography sx={{ fontFamily: "var(--theo-font-sans)", fontSize: 13.5 }}>
        <strong>3. In-scope no-op:</strong> tickets that were scoped but not actionable this pass because they were baseline, outside the active cursor window, unchanged, or already had poller state.
      </Typography>
      <Typography sx={{ fontFamily: "var(--theo-font-sans)", fontSize: 13.5 }}>
        <strong>4. Detected actionable event:</strong> a new ticket, recovery candidate, or eligibility change that should be classified.
      </Typography>
      <Typography sx={{ fontFamily: "var(--theo-font-sans)", fontSize: 13.5 }}>
        <strong>5. Classified:</strong> the rule result. It can qualify for Auto Agent, drop to fallback/no handling, or be marked dropped because no execution is needed.
      </Typography>
      <Typography sx={{ fontFamily: "var(--theo-font-sans)", fontSize: 13.5 }}>
        <strong>6. Execution:</strong> qualified tickets persist a Cloud Tasks payload, then queue, complete, or fail.
      </Typography>
    </Stack>
  </Paper>
);

const MetricCard: React.FC<{ label: string; value: number; helper?: string; onClick?: () => void }> = ({
  label,
  value,
  helper,
  onClick,
}) => (
  <Paper
    component={onClick ? "button" : "div"}
    type={onClick ? "button" : undefined}
    onClick={onClick}
    elevation={0}
    sx={{
      border: `1px solid ${BORDER}`,
      borderRadius: 2,
      p: 1.5,
      background: "#fff",
      minWidth: 150,
      flex: "1 1 150px",
      textAlign: "left",
      font: "inherit",
      color: "inherit",
      cursor: onClick ? "pointer" : "default",
      "&:hover": onClick ? { background: STRIP_BG } : undefined,
    }}
  >
    <Typography sx={{ fontFamily: "var(--theo-font-sans)", fontSize: 12, color: MUTED, fontWeight: 700 }}>
      {label}
    </Typography>
    <Typography sx={{ fontFamily: "var(--theo-font-sans)", fontSize: 24, color: "#000", fontWeight: 800, mt: 0.5 }}>
      {formatNumber(value)}
    </Typography>
    {helper ? (
      <Typography sx={{ fontFamily: "var(--theo-font-sans)", fontSize: 11.5, color: MUTED, mt: 0.25 }}>
        {helper}
      </Typography>
    ) : null}
  </Paper>
);

const FunnelEventTable: React.FC<{ rows: AutoAgentFunnelEvent[] }> = ({ rows }) => {
  if (rows.length === 0) {
    return (
      <Box sx={{ px: 2, py: 4, textAlign: "center" }}>
        <Typography sx={{ fontFamily: "var(--theo-font-sans)", fontSize: 14, color: MUTED }}>
          No poller funnel events found for this filter.
        </Typography>
      </Box>
    );
  }

  return (
    <TableContainer>
      <Table size="small" sx={{ minWidth: 1280 }}>
        <TableHead>
          <TableRow>
            <TableCell sx={{ fontFamily: "var(--theo-font-sans)", fontWeight: 700 }}>Ticket</TableCell>
            <TableCell sx={{ fontFamily: "var(--theo-font-sans)", fontWeight: 700 }}>Event Time</TableCell>
            <TableCell sx={{ fontFamily: "var(--theo-font-sans)", fontWeight: 700 }}>Stage</TableCell>
            <TableCell sx={{ fontFamily: "var(--theo-font-sans)", fontWeight: 700 }}>Decision</TableCell>
            <TableCell sx={{ fontFamily: "var(--theo-font-sans)", fontWeight: 700 }}>Rule / Reason</TableCell>
            <TableCell sx={{ fontFamily: "var(--theo-font-sans)", fontWeight: 700 }}>Fit</TableCell>
            <TableCell sx={{ fontFamily: "var(--theo-font-sans)", fontWeight: 700 }}>Theo Category</TableCell>
            <TableCell sx={{ fontFamily: "var(--theo-font-sans)", fontWeight: 700 }}>Customer</TableCell>
            <TableCell sx={{ fontFamily: "var(--theo-font-sans)", fontWeight: 700 }}>Scope</TableCell>
            <TableCell sx={{ fontFamily: "var(--theo-font-sans)", fontWeight: 700 }}>Result</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((row) => (
            <TableRow key={row.id} hover>
              <TableCell sx={{ maxWidth: 280 }}>
                <Typography
                  component="a"
                  href={`/supervisor/tickets/${encodeURIComponent(row.ticket_id)}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  sx={{
                    color: "#000",
                    display: "block",
                    fontFamily: "var(--theo-font-sans)",
                    fontSize: 14,
                    fontWeight: 700,
                    textDecoration: "none",
                    "&:hover": { color: BROWN, textDecoration: "underline" },
                  }}
                  noWrap
                >
                  {row.ticket_title || `Ticket ${row.ticket_id}`}
                </Typography>
                <Typography sx={{ fontFamily: "var(--theo-font-sans)", fontSize: 12, color: MUTED }}>
                  #{row.ticket_id}
                  {row.ticket_type ? ` · ${row.ticket_type}` : ""}
                </Typography>
              </TableCell>
              <TableCell>
                <Typography sx={{ fontFamily: "var(--theo-font-sans)", fontSize: 13 }}>
                  {formatDateTime(row.ticket_event_time || row.observed_at)}
                </Typography>
                <Typography sx={{ fontFamily: "var(--theo-font-sans)", fontSize: 11.5, color: MUTED }}>
                  Seen {formatDateTime(row.observed_at)}
                </Typography>
              </TableCell>
              <TableCell>
                <Chip
                  size="small"
                  label={formatStage(row.funnel_stage)}
                  color={row.funnel_stage === "qualified" ? "success" : row.funnel_stage === "execution_error" ? "error" : "default"}
                  variant="outlined"
                  sx={{ fontFamily: "var(--theo-font-sans)" }}
                />
              </TableCell>
              <TableCell>
                <Typography sx={{ fontFamily: "var(--theo-font-sans)", fontSize: 13.5 }}>
                  {formatDecision(row)}
                </Typography>
              </TableCell>
              <TableCell sx={{ maxWidth: 260 }}>
                <Typography sx={{ fontFamily: "var(--theo-font-sans)", fontSize: 13.5 }} noWrap>
                  {row.matched_rule_id || row.dropoff_reason || "-"}
                </Typography>
                {row.classification_reason || row.scope_reason ? (
                  <Typography sx={{ fontFamily: "var(--theo-font-sans)", fontSize: 11.5, color: MUTED }} noWrap>
                    {row.classification_reason || row.scope_reason}
                  </Typography>
                ) : null}
              </TableCell>
              <TableCell sx={{ maxWidth: 240 }}>
                <Typography sx={{ fontSize: 13.5 }}>
                  {row.fit ? row.fit.score === null ? "Unscored" : `${Math.round(row.fit.score * 100)}%` : "—"}
                </Typography>
                {row.fit ? <Typography sx={{ fontSize: 11.5, color: MUTED }}>Auto Mode requires &gt;{Math.round(row.fit.threshold * 100)}% fit</Typography> : null}
                {row.fit ? <Typography sx={{ fontSize: 11.5, color: MUTED }}>{row.fit.reason}</Typography> : null}
              </TableCell>
              <TableCell sx={{ maxWidth: 240 }}>
                <FunnelTheoCategoryCell row={row} />
              </TableCell>
              <TableCell>
                <Typography sx={{ fontFamily: "var(--theo-font-sans)", fontSize: 13.5 }}>
                  {row.customer_name || row.psa_customer_id || "-"}
                </Typography>
                <Typography sx={{ fontFamily: "var(--theo-font-sans)", fontSize: 11.5, color: MUTED }}>
                  {row.customer_auto_enabled ? "Auto enabled" : "Auto disabled/unknown"}
                </Typography>
              </TableCell>
              <TableCell>
                <Typography sx={{ fontFamily: "var(--theo-font-sans)", fontSize: 13.5 }}>
                  Queue {row.queue_id || "-"}
                </Typography>
                <Typography sx={{ fontFamily: "var(--theo-font-sans)", fontSize: 11.5, color: MUTED }}>
                  Type/Cat {row.category_id || "-"}
                  {row.psa_category_id ? ` · PSA ${row.psa_category_id}` : ""}
                </Typography>
              </TableCell>
              <TableCell sx={{ maxWidth: 240 }}>
                <Typography sx={{ fontFamily: "var(--theo-font-sans)", fontSize: 13.5 }} noWrap>
                  {row.execution_status || row.event_stage}
                </Typography>
                <Typography sx={{ fontFamily: "var(--theo-font-sans)", fontSize: 11.5, color: MUTED }} noWrap>
                  {row.ai_thread_id ? `Thread ${row.ai_thread_id}` : row.request_id || row.error_message || ""}
                </Typography>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

const PollerFunnelPanel: React.FC = () => {
  const [windowValue, setWindowValue] = useState("48h");
  const [stage, setStage] = useState("");
  const [ticketId, setTicketId] = useState("");
  const [customer, setCustomer] = useState("");
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [summary, setSummary] = useState<AutoAgentFunnelSummary | null>(null);
  const [rows, setRows] = useState<AutoAgentFunnelEvent[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  /** A background load is the live poll: no spinner, and a failure keeps the rows already shown. */
  const loadFunnel = useCallback(async (background = false) => {
    if (!background) {
      setLoading(true);
      setError(null);
    }
    try {
      const [summaryResponse, eventsResponse] = await Promise.all([
        integrationApi.getAutoAgentFunnelSummary(windowValue),
        integrationApi.getAutoAgentFunnelEvents({
          window: windowValue,
          stage,
          ticketId,
          customer,
          page: page + 1,
          pageSize,
        }),
      ]);
      setSummary(summaryResponse);
      setRows(eventsResponse.rows || []);
      setTotalCount(eventsResponse.totalCount || 0);
      setError(null);
    } catch (err: any) {
      if (!background) setError(err?.message || "Failed to load poller funnel.");
    } finally {
      if (!background) setLoading(false);
    }
  }, [customer, page, pageSize, stage, ticketId, windowValue]);

  useEffect(() => {
    void loadFunnel();
  }, [loadFunnel]);

  useVisiblePoll(() => loadFunnel(true));

  const totals = summary?.totals;
  const executedCount = Number((totals as any)?.executed_count || 0);
  const executionCompletedCount = Number((totals as any)?.execution_completed_count || 0);
  const executionFailedCount = Number((totals as any)?.execution_failed_count || totals?.execution_error_count || 0);
  const activeScopes = summary?.config?.active_scopes || [];
  const coverage = summary?.config?.customer_coverage;

  return (
    <Stack spacing={2}>
      <Paper elevation={0} sx={{ border: `1px solid ${BORDER}`, borderRadius: 2, p: 2, background: "#fff" }}>
        <Stack direction={{ xs: "column", md: "row" }} justifyContent="space-between" spacing={2}>
          <Box>
            <Typography sx={{ fontFamily: "var(--theo-font-sans)", fontSize: 20, fontWeight: 800 }}>
              Poller Funnel
            </Typography>
            <Typography sx={{ fontFamily: "var(--theo-font-sans)", fontSize: 12.5, color: MUTED, mt: 0.5 }}>
              Last poll {formatDateTime(summary?.latest_cycle?.completed_at || summary?.totals?.last_completed_at)}
              {summary?.latest_cycle?.next_cursor_at ? ` · Cursor ${summary.latest_cycle.next_cursor_at}` : ""}
              {summary?.latest_cycle?.last_error ? " · Last poll had an error" : " · Status healthy"}
            </Typography>
          </Box>
          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
            <TextField
              select
              size="small"
              label="Window"
              value={windowValue}
              onChange={(event) => {
                setPage(0);
                setWindowValue(event.target.value);
              }}
              sx={{ minWidth: 130 }}
            >
              {FUNNEL_WINDOWS.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </TextField>
            <Button
              variant="outlined"
              startIcon={loading ? <CircularProgress size={16} /> : <RefreshRoundedIcon />}
              disabled={loading}
              onClick={() => void loadFunnel()}
              sx={{ fontFamily: "var(--theo-font-sans)", textTransform: "none" }}
            >
              Refresh
            </Button>
          </Stack>
        </Stack>

        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mt: 2 }}>
          {activeScopes.length ? (
            activeScopes.map((scope) => (
              <Chip
                key={`${scope.platform_id}-${scope.scope_type}-${scope.queue_id}`}
                size="small"
                label={`${scope.platform_name} · ${scope.scope_type}: ${scope.scope_name || scope.queue_id}`}
                sx={{ fontFamily: "var(--theo-font-sans)", background: BROWN_TINT }}
              />
            ))
          ) : (
            <Chip size="small" label="No active auto scopes" sx={{ fontFamily: "var(--theo-font-sans)" }} />
          )}
          {coverage ? (
            <Chip
              size="small"
              label={`${coverage.inheriting_msp_default} inheriting default · ${coverage.explicit_customer_configs} overrides`}
              sx={{ fontFamily: "var(--theo-font-sans)" }}
            />
          ) : null}
        </Stack>
      </Paper>

      {error ? <Alert severity="error">{error}</Alert> : null}

      <Stack direction="row" spacing={1.5} flexWrap="wrap" useFlexGap>
        <MetricCard label="Fetched" value={totals?.fetched_count || 0} helper={`${formatNumber(totals?.cycle_count)} cycles`} />
        <MetricCard label="In Scope" value={totals?.scoped_count || 0} />
        <MetricCard label="Detected" value={totals?.events_detected_count || 0} />
        <MetricCard label="Qualified" value={totals?.qualified_count || 0} />
        <MetricCard label="Queued" value={executedCount} />
        <MetricCard label="Completed" value={executionCompletedCount} />
        <MetricCard label="Failed" value={executionFailedCount} />
        <MetricCard label="Dropped" value={totals?.dropped_count || 0} />
        <MetricCard label="Out of Scope" value={totals?.out_of_scope_count || 0} />
        <MetricCard label="Errors" value={totals?.error_count || 0} />
      </Stack>

      <FunnelHierarchyGuide />

      <Stack direction={{ xs: "column", lg: "row" }} spacing={2}>
        <Paper elevation={0} sx={{ border: `1px solid ${BORDER}`, borderRadius: 2, p: 2, background: "#fff", flex: 1 }}>
          <Typography sx={{ fontFamily: "var(--theo-font-sans)", fontSize: 16, fontWeight: 800, mb: 1 }}>
            Funnel Breakdown
          </Typography>
          <Stack spacing={0.75}>
            {(summary?.stages || []).map((item) => (
              <Stack key={item.funnel_stage} direction="row" justifyContent="space-between">
                <Typography sx={{ fontFamily: "var(--theo-font-sans)", fontSize: 13.5 }}>{formatStage(item.funnel_stage)}</Typography>
                <Typography sx={{ fontFamily: "var(--theo-font-sans)", fontSize: 13.5, fontWeight: 700 }}>
                  {formatNumber(item.count)}
                </Typography>
              </Stack>
            ))}
            {summary && summary.stages.length === 0 ? (
              <Typography sx={{ fontFamily: "var(--theo-font-sans)", fontSize: 13, color: MUTED }}>
                No poller cycles recorded in this window yet.
              </Typography>
            ) : null}
          </Stack>
        </Paper>

        <Paper elevation={0} sx={{ border: `1px solid ${BORDER}`, borderRadius: 2, p: 2, background: "#fff", flex: 1 }}>
          <Typography sx={{ fontFamily: "var(--theo-font-sans)", fontSize: 16, fontWeight: 800, mb: 1 }}>
            Top Dropoff Reasons
          </Typography>
          <Stack spacing={0.75}>
            {(summary?.dropoff_reasons || []).map((item) => (
              <Stack key={item.reason} direction="row" justifyContent="space-between" spacing={2}>
                <Typography sx={{ fontFamily: "var(--theo-font-sans)", fontSize: 13.5 }} noWrap>
                  {item.reason}
                </Typography>
                <Typography sx={{ fontFamily: "var(--theo-font-sans)", fontSize: 13.5, fontWeight: 700 }}>
                  {formatNumber(item.count)}
                </Typography>
              </Stack>
            ))}
            {summary && summary.dropoff_reasons.length === 0 ? (
              <Typography sx={{ fontFamily: "var(--theo-font-sans)", fontSize: 13, color: MUTED }}>
                No dropoffs recorded in this window.
              </Typography>
            ) : null}
          </Stack>
        </Paper>
      </Stack>

      <Paper elevation={0} sx={{ border: `1px solid ${BORDER}`, borderRadius: 2, background: "#fff", overflow: "hidden" }}>
        <Box sx={{ p: 2, background: STRIP_BG, borderBottom: `1px solid ${BORDER}` }}>
          <Stack direction={{ xs: "column", md: "row" }} spacing={1.5} alignItems={{ xs: "stretch", md: "center" }}>
            <TextField
              select
              size="small"
              label="Stage"
              value={stage}
              onChange={(event) => {
                setPage(0);
                setStage(event.target.value);
              }}
              sx={{ minWidth: 170 }}
            >
              {FUNNEL_STAGES.map((option) => (
                <MenuItem key={option.value || "all"} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              size="small"
              label="Ticket ID"
              value={ticketId}
              onChange={(event) => {
                setPage(0);
                setTicketId(event.target.value);
              }}
            />
            <TextField
              size="small"
              label="Customer"
              value={customer}
              onChange={(event) => {
                setPage(0);
                setCustomer(event.target.value);
              }}
            />
            {loading ? <CircularProgress size={20} sx={{ color: BROWN }} /> : null}
          </Stack>
        </Box>
        <FunnelEventTable rows={rows} />
        <TablePagination
          component="div"
          count={totalCount}
          page={page}
          onPageChange={(_, nextPage) => setPage(nextPage)}
          rowsPerPage={pageSize}
          rowsPerPageOptions={[10, 25, 50, 100]}
          onRowsPerPageChange={(event) => {
            setPage(0);
            setPageSize(Number.parseInt(event.target.value, 10));
          }}
        />
      </Paper>
    </Stack>
  );
};

function useDebouncedValue<T>(value: T, delayMs: number): T {
  const [debounced, setDebounced] = useState(value);
  const valueRef = useRef(value);
  valueRef.current = value;
  const serialized = JSON.stringify(value);
  useEffect(() => {
    const handle = window.setTimeout(() => {
      const next = valueRef.current;
      setDebounced((current) => (JSON.stringify(current) === JSON.stringify(next) ? current : next));
    }, delayMs);
    return () => window.clearTimeout(handle);
  }, [serialized, delayMs]);
  return debounced;
}

const TheoAutomationsMonitor: React.FC<{ agentId?: number }> = ({ agentId: requestedAgentId }) => {
  const { userMspInfo } = useUserMsp();
  const { agents, isLoading: agentsLoading } = useAgents(userMspInfo?.msp_id);
  const agentId = requestedAgentId ?? agents.find((agent) => agent.is_default)?.id;
  const canViewPollerFunnel = isHorizonRootRole(userMspInfo?.role);
  const [activeTab, setActiveTab] = useState<"summary" | "executions" | "funnel">("executions");
  const [filters, setFilters] = useState<ExecutionFilters>(EMPTY_EXECUTION_FILTERS);
  const textFilters = useMemo(
    () => ({
      ticketQuery: filters.ticketQuery,
      startedAfter: filters.startedAfter,
      startedBefore: filters.startedBefore,
    }),
    [filters.ticketQuery, filters.startedAfter, filters.startedBefore]
  );
  const debouncedText = useDebouncedValue(textFilters, 300);
  const effectiveFilters = useMemo<ExecutionFilters>(
    () => ({
      ...filters,
      ticketQuery: debouncedText.ticketQuery,
      startedAfter: debouncedText.startedAfter,
      startedBefore: debouncedText.startedBefore,
    }),
    [debouncedText, filters]
  );
  const filterQuery = useMemo(() => buildMonitorQueryParams(effectiveFilters), [effectiveFilters]);
  const filterKey = JSON.stringify(filterQuery);
  const filterQueryRef = useRef(filterQuery);
  filterQueryRef.current = filterQuery;
  const [filterOptions, setFilterOptions] = useState<AutoAgentMonitorFilterOptions | null>(null);
  const [filterOptionsError, setFilterOptionsError] = useState<string | null>(null);
  const [summaryRefreshToken, setSummaryRefreshToken] = useState(0);
  const [queueRefreshToken, setQueueRefreshToken] = useState(0);

  const loadFilterOptions = useCallback(async () => {
    if (!agentId) return;
    try {
      const response = await integrationApi.getAutoAgentMonitorFilterOptions(agentId);
      setFilterOptions(response);
      setFilterOptionsError(null);
    } catch (error: any) {
      setFilterOptionsError(error?.message || "Failed to load filter options.");
    }
  }, [agentId]);

  useEffect(() => {
    void loadFilterOptions();
  }, [loadFilterOptions]);

  useEffect(() => {
    if (!canViewPollerFunnel && activeTab === "funnel") {
      setActiveTab("executions");
    }
  }, [activeTab, canViewPollerFunnel]);

  const openExecutions = useCallback((patch: Partial<ExecutionFilters>) => {
    setFilters({ ...EMPTY_EXECUTION_FILTERS, ...patch });
    setActiveTab("executions");
  }, []);

  if (!agentId) return <Box sx={{ p: 3 }}>{agentsLoading ? "Loading agent…" : "Agent unavailable."}</Box>;

  return (
    <Box sx={{ width: "100%", minHeight: "100%", background: NEUTRAL.page }}>
      <Box sx={{ px: { xs: 2, md: 3 }, py: { xs: 2, md: 2.5 } }}>
        <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={2} sx={{ mb: 2.5 }}>
          <Box>
            <Typography sx={{ fontFamily: "var(--theo-font-sans)", fontSize: { xs: 26, md: 32 }, fontWeight: 800, color: "#000" }}>
              Auto Agent Monitor
            </Typography>
          </Box>
          <Button
            variant="contained"
            startIcon={<RefreshRoundedIcon />}
            onClick={() => {
              setSummaryRefreshToken((value) => value + 1);
              setQueueRefreshToken((value) => value + 1);
              void loadFilterOptions();
            }}
            sx={{
              fontFamily: "var(--theo-font-sans)",
              textTransform: "none",
              background: BROWN,
              "&:hover": { background: VIOLET.dark },
            }}
          >
            Refresh
          </Button>
        </Stack>

        <Paper elevation={0} sx={{ border: `1px solid ${BORDER}`, borderRadius: 2, mb: 2, background: "#fff" }}>
          <Tabs
            value={activeTab}
            onChange={(_, value) => setActiveTab(value)}
            sx={{
              minHeight: 44,
              px: 1,
              "& .MuiTab-root": {
                fontFamily: "var(--theo-font-sans)",
                textTransform: "none",
                fontWeight: 700,
                minHeight: 44,
              },
              "& .Mui-selected": { color: `${BROWN} !important` },
              "& .MuiTabs-indicator": { backgroundColor: BROWN },
            }}
          >
            <Tab label="Summary" value="summary" />
            <Tab label="Executions" value="executions" />
            {canViewPollerFunnel ? <Tab label="Poller Funnel" value="funnel" /> : null}
          </Tabs>
        </Paper>

        {activeTab === "summary" ? (
          <SummaryPanel agentId={agentId} refreshToken={summaryRefreshToken} onOpenExecutions={openExecutions} />
        ) : null}
        {activeTab === "executions" ? (
          <Stack spacing={2}>
            <ExecutionFilterBar
              filters={filters}
              options={filterOptions}
              optionsError={filterOptionsError}
              onChange={(patch) => setFilters((current) => ({ ...current, ...patch }))}
              onClear={() => setFilters(EMPTY_EXECUTION_FILTERS)}
            />
            <ExecutionQueue
              agentId={agentId}
              filterQuery={filterQuery}
              filterKey={filterKey}
              refreshToken={queueRefreshToken}
            />
          </Stack>
        ) : null}
        {activeTab === "funnel" && canViewPollerFunnel ? <PollerFunnelPanel /> : null}
      </Box>
    </Box>
  );
};

export default TheoAutomationsMonitor;
